import { z } from 'zod';

// Cargo ("Грузы") registry enums — faithful NextKargo port (#1227). The
// member identifiers are the WIRE/DB contract; the Russian display labels
// (the exact source strings) live in the `*_LABELS` Records for UI/PDF reuse
// and are never the source of truth for branching.
//
// `CargoStatus`, `CargoClientPaymentStatus`, `CargoCarrierPaymentStatus`, and
// `CargoImportFileFormat` are mirrored 1:1 by Prisma enums (identifier-valued)
// in `apps/backend/prisma/schema/cargo.prisma` — the member identifiers MUST
// match exactly. `CargoCarrier` and `CargoPathFormat` are NOT Prisma enums:
// the source stores them as free CharField choices whose values contain spaces
// and Cyrillic (e.g. 'Контейнер 40'), so they are persisted as `String?`
// columns and validated at the app layer by these Zod enums instead.

/**
 * @aiPurpose stored Cargo registry status. NextKargo toggles exactly two
 *   values; there is NO `issued`/«Выдан» status — issuance is derived solely
 *   from the release date (`timeCargoRelease`).
 * @aiInvariant member identifiers mirror the Prisma `CargoStatus` enum 1:1.
 */
export const CARGO_STATUSES = ['in_transit', 'arrived_rf'] as const;
export const CargoStatusSchema = z.enum(CARGO_STATUSES);
export type CargoStatus = z.infer<typeof CargoStatusSchema>;
export const CARGO_STATUS_LABELS: Record<CargoStatus, string> = {
  in_transit: 'В пути',
  arrived_rf: 'Прибыл в РФ',
};

/**
 * @aiPurpose client-payment status. Mirrors the Prisma
 *   `CargoClientPaymentStatus` enum 1:1 (4 members).
 * @aiInvariant `under_review` is the workflow review state, set ONLY by the
 *   backend financier review flow (#1936); it is intentionally excluded from
 *   the manual/quick write surfaces — see `CARGO_CLIENT_PAYMENT_MANUAL_STATUSES`.
 */
export const CARGO_CLIENT_PAYMENT_STATUSES = [
  'not_paid',
  'partially_paid',
  'paid',
  'under_review',
] as const;
export const CargoClientPaymentStatusSchema = z.enum(CARGO_CLIENT_PAYMENT_STATUSES);
export type CargoClientPaymentStatus = z.infer<typeof CargoClientPaymentStatusSchema>;
export const CARGO_CLIENT_PAYMENT_STATUS_LABELS: Record<CargoClientPaymentStatus, string> = {
  not_paid: 'Не оплачено',
  partially_paid: 'Оплачено частично',
  paid: 'Оплачено полностью',
  under_review: 'На проверке',
};

/**
 * @aiPurpose the manually-settable client-payment statuses (legacy write
 *   surfaces). Excludes `under_review`, which is set ONLY by the financier
 *   review workflow (#1936), never by a direct manual/quick status write.
 */
export const CARGO_CLIENT_PAYMENT_MANUAL_STATUSES = ['not_paid', 'partially_paid', 'paid'] as const;
export const CargoClientPaymentManualStatusSchema = z.enum(CARGO_CLIENT_PAYMENT_MANUAL_STATUSES);
export type CargoClientPaymentManualStatus = z.infer<typeof CargoClientPaymentManualStatusSchema>;

/**
 * @aiPurpose manual carrier-payment status. Mirrors the Prisma
 *   `CargoCarrierPaymentStatus` enum 1:1 (binary in the source).
 */
export const CARGO_CARRIER_PAYMENT_STATUSES = ['not_paid', 'paid'] as const;
export const CargoCarrierPaymentStatusSchema = z.enum(CARGO_CARRIER_PAYMENT_STATUSES);
export type CargoCarrierPaymentStatus = z.infer<typeof CargoCarrierPaymentStatusSchema>;
export const CARGO_CARRIER_PAYMENT_STATUS_LABELS: Record<CargoCarrierPaymentStatus, string> = {
  not_paid: 'Не оплачено',
  paid: 'Оплачено',
};

/**
 * @aiPurpose import-file format selector. Mirrors the Prisma
 *   `CargoImportFileFormat` enum 1:1. `yan` is the ordinary Yan workbook;
 *   `yan_full_truck` is the full-machine («полная машина») variant.
 */
export const CARGO_IMPORT_FILE_FORMATS = ['yan', 'yan_full_truck'] as const;
export const CargoImportFileFormatSchema = z.enum(CARGO_IMPORT_FILE_FORMATS);
export type CargoImportFileFormat = z.infer<typeof CargoImportFileFormatSchema>;
export const CARGO_IMPORT_FILE_FORMAT_LABELS: Record<CargoImportFileFormat, string> = {
  yan: 'Ян',
  yan_full_truck: 'Ян - контейнер',
};

/**
 * @aiPurpose payment-document review state (#1933, Workstream B). A workflow
 *   contract, NOT a Prisma-mirrored enum: the new financier review state for a
 *   cargo client-payment document. `under_review` is intentionally absent here
 *   (deferred to #1934).
 */
export const CARGO_PAYMENT_DOC_REVIEW_STATUSES = ['pending', 'accepted', 'rejected'] as const;
export const CargoPaymentDocReviewStatusSchema = z.enum(CARGO_PAYMENT_DOC_REVIEW_STATUSES);
export type CargoPaymentDocReviewStatus = z.infer<typeof CargoPaymentDocReviewStatusSchema>;
export const CARGO_PAYMENT_DOC_REVIEW_STATUS_LABELS: Record<CargoPaymentDocReviewStatus, string> = {
  pending: 'Не проверен',
  accepted: 'Проверен',
  rejected: 'Отклонён',
};

/**
 * @aiPurpose payment currency for a reviewed cargo client-payment document
 *   (#1933, Workstream B). A workflow contract, NOT a Prisma-mirrored enum.
 */
export const CARGO_PAYMENT_CURRENCIES = ['RUB', 'USD', 'CNY'] as const;
export const CargoPaymentCurrencySchema = z.enum(CARGO_PAYMENT_CURRENCIES);
export type CargoPaymentCurrency = z.infer<typeof CargoPaymentCurrencySchema>;

/**
 * @aiPurpose closed catalog of registry carrier choices. NOT a Prisma enum
 *   and NOT a FK to the carriers dictionary — the source stores `carrier` as
 *   a hardcoded CharField choice (the dictionary `CarriersList` is a separate
 *   calculator-only catalog). Stored as `String?`; this Zod enum is the
 *   app-level guard. The literal values are the exact source strings.
 * @aiInvariant exactly 15 values, order is the source choice order.
 */
export const CARGO_CARRIERS = [
  'Ян',
  'Байкал',
  'URAL',
  'X',
  'Мурад',
  'Дин',
  'ИВАН',
  'Слава',
  'BST',
  'Лиля',
  'Роман',
  'Людмила',
  'Суньфенхэ',
  'Пешков',
  'DNS',
] as const;
export const CargoCarrierSchema = z.enum(CARGO_CARRIERS);
export type CargoCarrier = z.infer<typeof CargoCarrierSchema>;

/**
 * @aiPurpose closed catalog of registry path-format («Формат пути») choices.
 *   Same rationale as `CARGO_CARRIERS`: a hardcoded source CharField choice
 *   stored as `String?`, validated by this Zod enum. The literal values are
 *   the exact source strings.
 * @aiInvariant exactly 10 values, order is the source choice order.
 */
export const CARGO_PATH_FORMATS = [
  'Быстрое авто',
  'Обычное авто',
  'Уссурийск',
  'Минск',
  'Суньфенхэ',
  'ЖД',
  'Авиа',
  'Контейнер 40',
  'Контейнер 20',
  'Фура 90',
] as const;
export const CargoPathFormatSchema = z.enum(CARGO_PATH_FORMATS);
export type CargoPathFormat = z.infer<typeof CargoPathFormatSchema>;
